import { useState, useEffect, useRef } from "react";
import { useLoadScript } from "@react-google-maps/api";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import {
  closeAddAnotherAddress,
  openAddressFromMap,
} from "../../store/slices/modalSlice";
import { MapPin } from "lucide-react";
import AddressFromMap from "../selectAddressFromMapModel/AddressFromMap";
import GoogleMapReact from "google-map-react";
import { prediction, selectedPlace, suggestion } from "../../types/booking";

const libraries: "places"[] = ["places"];

const googleMapsApiKey = import.meta.env.VITE_Google_Maps_Api_Key;

const AddAnotherAddressModel = () => {
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapsRef = useRef<typeof google.maps | null>(null);
  const [center, setCenter] = useState({ lat: 37.7749, lng: -122.4194 });
  const [zoom, setZoom] = useState(16);
  const [suggestions, setSuggestions] = useState<suggestion[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<selectedPlace | null>(
    null
  );
  const [error, setError] = useState(null);
  const [predictions, setPredictions] = useState<prediction[]>();
  const showAddAddress = useSelector(
    (state: { modal: { addAnotherAddress: boolean } }) =>
      state.modal.addAnotherAddress
  );

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
    libraries,
  });

  const handleApiLoaded = (map: google.maps.Map, maps: typeof google.maps) => {
    autocompleteServiceRef.current = new maps.places.AutocompleteService();
    mapRef.current = map;
    mapsRef.current = maps;

 
 

  };

  const cleanAddress = (address: string | undefined) => {
    const parts = address?.split(" - ");
    return parts
      ?.filter((part) => !/^[A-Za-z0-9+]{2,}$/.test(part))
      .join(" - ");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    autocompleteServiceRef.current =
      new google.maps.places.AutocompleteService();
    if (query && autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        { input: query },
        (predictions, status) => {
          if (status === "OK" && predictions) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (placeId: string) => {
    if (!mapRef.current || !mapsRef.current) return;
    const placesService = new mapsRef.current.places.PlacesService(
      mapRef.current
    );
    placesService.getDetails({ placeId }, (place, status) => {
      if (status === "OK" && place?.geometry?.location) {
        const newCenter = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setCenter(newCenter);
        setSelectedPlace({
          name: place?.name,
          address: cleanAddress(place.formatted_address),
          coordinates: {
            lat: newCenter.lat,
            lng: newCenter.lng,
          },
        });


        const theSelectedPlace = {
          name: place.name,
          address: cleanAddress(place.formatted_address),
          coordinates: newCenter,
        };

        const theSuggestions = {
          name: place.name,
          address: cleanAddress(place.formatted_address),
          coordinates: newCenter,
          place_id: placeId,
        };

        const suggestion = sessionStorage.getItem("suggestions");
        if (suggestion) {
          const newSuggestion = JSON.parse(suggestion);

          const isPresent = newSuggestion.some(
            (item: prediction) => item.place_id === theSuggestions.place_id
          );
          if (!isPresent) {
            newSuggestion.unshift(theSuggestions);
            if (newSuggestion.length > 8) {
              newSuggestion.pop(); 
            }

            sessionStorage.setItem(
              "suggestions",
              JSON.stringify(newSuggestion)
            );
          }
        } else {
          sessionStorage.setItem(
            "suggestions",
            JSON.stringify([theSuggestions])
          );
        }

        sessionStorage.setItem(
          "selectedPlace",
          JSON.stringify(theSelectedPlace)
        );

        dispatch(closeAddAnotherAddress());
        dispatch(openAddressFromMap());
      }
    });
  };

  const useCurrentLocation = () => {

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCenter({ lat: latitude, lng: longitude });
          setError(null); 

          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
          fetch(url)
            .then((res) => res.json())
            .then((data) => {
       
              const newCenter = {
                lat: latitude,
                lng: longitude,
              };

              setSelectedPlace({
                name: data.address.residential ?? data.town,
                address: data.display_name,
                coordinates: {
                  lat: newCenter.lat,
                  lng: newCenter.lng,
                },
              });

              const theSelectedPlace = {
                name: data.address.residential ?? data.town,
                address: data.display_name,
                coordinates: newCenter,
              };

              sessionStorage.setItem(
                "selectedPlace",
                JSON.stringify(theSelectedPlace)
              );

              dispatch(closeAddAnotherAddress());
              dispatch(openAddressFromMap());
            });
        },
        (error) => {
          console.error("Error fetching location:", error);
        }
      );
    }
  };

  useEffect(() => {
    inputRef.current?.focus();
    const suggestion = sessionStorage.getItem("suggestions");
    if (suggestion) {
      const suggestionParse = JSON.parse(suggestion);
      setPredictions(suggestionParse);
    }
  }, []);

  if (loadError) {
    return <div className="text-red-500">Error loading maps</div>;
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <MapPin className="w-8 h-8 text-blue-500 animate-bounce" />
      </div>
    );
  }

  const closeAddAddress = () => {
    sessionStorage.removeItem("mode");
    localStorage.removeItem("filteredData");
    dispatch(closeAddAnotherAddress());
  };

  return (
    <>
      {isLoaded ? (
        <Modal
          show={showAddAddress}
          onHide={closeAddAddress}
          centered
          className="mb-fix slide-from-bottom"
          backdrop="static"
        >
          <Modal.Header closeButton />
          <Modal.Body className="rounded">
            <div className="">
              <p className="font-17 ff-semibold text-black">
                Please select location
              </p>
              <div className="d-flex align-items-center border col-12 rounded mb-3">
                <div className="search-icon bg-transparent ms-2">
                  <img
                    src="https://goodhand.b-cdn.net/Assets/cart/cart-flow/search.svg"
                    alt=""
                  />
                </div>
                <input
                  ref={inputRef}
                  placeholder="Search for a place"
                  className="form-control font-13 border-0 ps-1"
                  onChange={handleInputChange}
                />
              </div>

              {selectedPlace && <AddressFromMap />}

              <>
                <div
                  className="d-flex flex-wrap align-items-center gap-2 pt-2 pb-2 mb-3   bg-3 p-2 px-3 rounded-2"
                  onClick={useCurrentLocation}
                >
                  <div>
                    <img
                      src="https://goodhand.b-cdn.net/Assets/cart/cart-flow/radar.svg"
                      alt=""
                    />
                  </div>
                  <div>
                    <p className="mb-0 text-main ff-medium font-15 cursor-pointer">
                      Use current location
                    </p>
                  </div>
                  {error && (
                    <p style={{ color: "red", marginBottom: "10px" }}>
                      {error}
                    </p>
                  )}
                </div>
              </>

              {suggestions.length > 0 ? (
                <>
                  {suggestions &&
                    suggestions?.map((item) => (
                      <div
                        className="location-container d-flex align-items-center gap-4 address-main border-bottom py-3"
                        style={{ cursor: "pointer" }}
                        key={item?.place_id}
                        onClick={() => handleSuggestionClick(item?.place_id)}
                      >
                        <div className="icon-wrapper  bg-light rounded ">
                          <div
                            aria-disabled="true"
                            role="img"
                            className="icon-center"
                          >
                            <img
                              src="https://goodhand.b-cdn.net/Assets/cart/cart-flow/home.svg"
                              alt=""
                            />
                          </div>
                        </div>

                        <div className="d-flex flex-column align-items-start ">
                          <p className="ff-semibold mb-0 location-title font-12 text-black">
                            {item?.description}
                          </p>
                         
                        </div>
                      </div>
                    ))}
                </>
              ) : (
                <>
                  {predictions && predictions?.length > 0 ? (
                    <>
                      {predictions?.map((prediction) => (
                        <div
                          className="location-container d-flex align-items-center gap-4 address-main border-bottom py-3"
                          key={prediction?.place_id}
                        >
                          <div className="icon-wrapper bg-light rounded p-3">
                            <div
                              aria-disabled="true"
                              role="img"
                              className="icon-center"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="#545454"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10.195 3.178A9.25 9.25 0 115.46 18.79l1.414-1.415a7.25 7.25 0 100-10.253L5.247 8.75H8v2H3a1 1 0 01-1-1v-5h2v2.419l1.46-1.46a9.25 9.25 0 014.735-2.531z"
                                  fill="#545454"
                                ></path>
                                <path
                                  d="M11 12.5V7h2v4.908l3.584 1.965-.962 1.754-4.103-2.25A1 1 0 0111 12.5z"
                                  fill="#545454"
                                ></path>
                              </svg>
                            </div>
                          </div>

                          <div
                            className="d-flex flex-column align-items-start "
                            onClick={() =>
                              handleSuggestionClick(prediction.place_id)
                            }
                            style={{ cursor: "pointer" }}
                          >
                            <p className="ff-semibold mb-0 location-title font-12 text-black">
                              {prediction?.address}
                            </p>
                     
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div className="error-location text-center">
                        <img
                          src="https://goodhand.b-cdn.net/Assets/cart/cart-flow/noLocation.svg"
                          alt=""
                        />

                        <p className="my-3 font-16 ff-semibold">
                          Location could not be fetched
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}

              <GoogleMapReact
                bootstrapURLKeys={{
                  key: googleMapsApiKey,
                  libraries: ["places"],
                }}
               
                defaultCenter={center}
                defaultZoom={zoom}
                center={center}
                zoom={zoom}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) =>
                  handleApiLoaded(map, maps)
                }
                onChange={({ center, zoom }) => {
                  setCenter(center);
                  setZoom(zoom);
                }}
              />
            </div>
          </Modal.Body>
        </Modal>
      ) : (
        <p></p>
      )}
    </>
  );
};

export default AddAnotherAddressModel;
